import { useEffect, useState } from "react";
import {
  FileText,
  Download,
  ArrowDownWideNarrow,
  ArrowDownNarrowWide,
  SquareX,
  Plus,
  CircleX,
  ListFilter,
} from "lucide-react";
import { useToast } from "./components/ToastContext";
import { getUserMessage } from "./services/api";

const INITIAL_FORM = {
  title: "",
  company: "",
  department: "",
  location: "",
  url: "",
  application_date: new Date().toISOString().split("T")[0],
  applied: true,
  application_type: "manual",
  resume: null,
  resumeName: null,
  cover_letter: false,
  referred: false,
  tailored: false,
  result: "pending",
};

const AddApplicationDialog = ({ isLoading, onSubmit, onClose }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitStatus, setSubmitStatus] = useState(null);

  const update = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = () =>
        setForm((prev) => ({
          ...prev,
          resume_used: reader.result.split(",")[1],
          resume_name: file.name,
        }));
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onSubmit(form);
      setForm(INITIAL_FORM);
      setSubmitStatus(null);
    } catch (error) {
      setSubmitStatus(error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 new-application-form">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Add New Job Application
          </h3>
          <button onClick={onClose} className="buttons-red-mini">
            <CircleX className="w-5 h-5" />
          </button>
        </div>
        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Job Title</label>
            <input
              type="text"
              required
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="Job title"
            />
          </div>
          <div className="form-group grid grid-cols-2 gap-4">
            <div>
              <label>Company</label>
              <input
                type="text"
                required
                value={form.company}
                onChange={(e) => update("company", e.target.value)}
                placeholder="Company Name"
              />
            </div>
            <div>
              <label>Department</label>
              <input
                type="text"
                value={form.department}
                onChange={(e) => update("department", e.target.value)}
                placeholder="Department Name"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Location</label>
            <input
              type="text"
              value={form.location}
              onChange={(e) => update("location", e.target.value)}
              placeholder="e.g. San Francisco, Remote"
            />
          </div>
          <div className="form-group">
            <label>Job URL</label>
            <input
              type="text"
              value={form.url}
              onChange={(e) => update("url", e.target.value)}
              placeholder="https://..."
            />
          </div>
          <div className="form-group">
            <label>Applied At</label>
            <input
              type="date"
              required
              max={new Date().toISOString().split("T")[0]}
              value={form.application_date}
              onChange={(e) => update("application_date", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Upload resume used</label>
            <input type="file" onChange={handleFileChange} />
          </div>
          <div className="form-group buttons-container gap-2">
            <label>Got Referred?</label>
            <input
              type="checkbox"
              checked={form.referred}
              onChange={(e) => update("referred", e.target.checked)}
              className="h-4 w-4 rounded"
              style={{ width: "20%" }}
            />
          </div>
          <div className="form-group buttons-container gap-2">
            <label>Used Cover Letter?</label>
            <input
              type="checkbox"
              checked={form.cover_letter}
              onChange={(e) => update("cover_letter", e.target.checked)}
              className="h-4 w-4 rounded"
              style={{ width: "20%" }}
            />
          </div>
          <div className="form-group">
            <label>Result</label>
            <select
              value={form.result}
              onChange={(e) => update("result", e.target.value)}
            >
              <option value="pending">Pending</option>
              <option value="rejected">Rejected (Initial)</option>
              <option value="phoneScreen">Phone Screen</option>
              <option value="phoneRejected">Rejected (Phone)</option>
              <option value="technical">Technical Interview</option>
              <option value="techRejected">Rejected (Tech)</option>
              <option value="onsite">Onsite Interview</option>
              <option value="onsiteRejected">Rejected (Onsite)</option>
              <option value="offers">Got an offer</option>
              <option value="accepted">Accepted Offer</option>
            </select>
          </div>
          <div className="buttons-container">
            <button
              type="submit"
              className="buttons-indigo"
              disabled={isLoading}
            >
              {isLoading ? "Adding..." : "Add Application"}
            </button>
          </div>
          {submitStatus !== null && (
            <div className="alert alert-error">
              Failed to save this application. Please try again later.
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

const ApplicationHistoryScreen = ({
  userEmail,
  getAllApplications,
  applicationHistory,
  setApplicationHistory,
  handleSaveJobApplication,
  handleDeleteApplication,
  handleDownloadPdf,
  handleDownloadCoverLetter,
  subscriptionCounts,
  setUserStats,
  isLoading,
  setIsLoading,
  isGeneratingCoverLetter,
  navigateTo,
  handleErrors,
}) => {
  const toast = useToast();
  const [savingAppId, setSavingAppId] = useState(null);
  const [searchedApps, setSearchedApps] = useState([]);
  const [sorted, setSorted] = useState({});
  const [showAddApplicationDialog, setShowAddApplicationDialog] =
    useState(false);
  const [appBeingModified, setAppBeingModified] = useState(null);

  useEffect(() => {
    getApplicationHistory();
  }, [userEmail]);

  const handleSortByField = (field) => {
    const currentDir = sorted[field] ?? 0;
    const newDir = currentDir === 0 ? 1 : 0;
    setSorted({ [field]: newDir });

    setSearchedApps((prev) =>
      [...prev].sort((a, b) => {
        let result;
        if (["created_at", "application_date", "tailored_at"].includes(field)) {
          result = new Date(a[field]) - new Date(b[field]);
        } else if (["ats_score", "match_score"].includes(field)) {
          result = (a[field] || 0) - (b[field] || 0);
        } else {
          result = String(a[field] || "").localeCompare(String(b[field] || ""));
        }
        return newDir === 0 ? result : -result;
      }),
    );
  };

  const getApplicationHistory = async () => {
    if (!userEmail) return;
    setIsLoading(true);

    try {
      const result = await getAllApplications(userEmail);

      if (!result.error && result.applications) {
        setApplicationHistory(result.applications);
        setSearchedApps(
          result.applications.sort(
            (a, b) => new Date(b["tailored_at"]) - new Date(a["tailored_at"]),
          ),
        );
      } else {
        setApplicationHistory([]);
        setSearchedApps([]);
      }
    } catch (error) {
      console.error("Error fetching application history");
      await handleErrors("Error fetching application history", error);
      setApplicationHistory([]);
      setSearchedApps([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplicationUpdate = async (appId, updates) => {
    const normalizedUpdates = { ...updates };
    if ("applied" in normalizedUpdates) {
      normalizedUpdates.application_date = normalizedUpdates.applied
        ? new Date().toISOString()
        : "";
    }

    setSavingAppId(appId);
    try {
      // Update in database
      await handleSaveJobApplication({
        application_id: appId,
        ...normalizedUpdates,
      });
      const updater = (prev) =>
        prev.map((app) =>
          app.application_id === appId ? { ...app, ...normalizedUpdates } : app,
        );
      setSearchedApps(updater);
      setApplicationHistory(updater);
    } catch (error) {
      console.error("Error updating application result");
      await handleErrors("Error updating application result", error);
    } finally {
      setSavingAppId(null);
    }
  };

  const handleSearchInputChange = (e) => {
    const searchQuery = e.target.value.toLowerCase();

    if (!searchQuery.trim()) {
      setSearchedApps(applicationHistory); // Show all applications if search is empty
      handleSortByField(false);
      return;
    }

    const filtered = searchedApps.filter((app) => {
      return (
        app.title?.toLowerCase().includes(searchQuery) ||
        app.company?.toLowerCase().includes(searchQuery) ||
        app.location?.toLowerCase().includes(searchQuery)
      );
    });

    setSearchedApps(filtered);
  };

  const deleteApplication = async (app) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the application for ${app.title}? This action cannot be undone.`,
    );

    if (!confirmed) return;

    try {
      await handleDeleteApplication(app.application_id);
      setApplicationHistory((prev) =>
        prev.filter((job) => job.application_id !== app.application_id),
      );
      setSearchedApps((prev) =>
        prev.filter((job) => job.application_id !== app.application_id),
      );
    } catch (error) {
      toast.error(getUserMessage(error));
      await handleErrors("Unable to delete application", error);
    }
  };

  return (
    <div className="bg-gray-50 p-4 flex-1">
      <div className="h-full max-w-6xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-4">
          {showAddApplicationDialog && (
            <AddApplicationDialog
              isLoading={isLoading}
              onSubmit={async (formData) => {
                setIsLoading(true);
                try {
                  const result = await handleSaveJobApplication(formData);
                  setApplicationHistory((prev) => [...prev, result]);
                  setSearchedApps((prev) => [...prev, result]);
                  setShowAddApplicationDialog(false);
                } catch (error) {
                  await handleErrors("Error saving new application", error);
                  throw error;
                } finally {
                  setIsLoading(false);
                }
              }}
              onClose={() => setShowAddApplicationDialog(false)}
            />
          )}
          <div className="flex items-center justify-between mb-2">
            <h2 className="applicant-theme-text">Application History</h2>
          </div>
          {/* {searchedApps.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg mb-2">
              <svg ref={svgRef} className="w-full"></svg>
            </div>
          )} */}
          <div className="flex justify-between items-center mb-4">
            <input
              type="text"
              placeholder="Search for jobs, companies, or locations..."
              onChange={handleSearchInputChange}
              className="max-w-lg px-4 py-2 border border-gray-300 rounded-lg"
            />
            <button
              className="buttons-indigo"
              onClick={() => setShowAddApplicationDialog(true)}
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          <div className="bg-loading-color rounded-lg shadow-lg p-2">
            {isLoading ? (
              <div className="buttons-container">
                <img
                  src="/assets/Applicant-loading.png"
                  className="w-25 h-25 max-auto mb-2"
                  alt="Applicant loading logo"
                />
              </div>
            ) : applicationHistory?.length === 0 ? (
              <div className="text-center p-4">
                <FileText className="w-16 h-16 text-indigo-600 mx-auto mb-4" />
                <p className="text-indigo-600">
                  No applications yet. Reset your filters or start applying to
                  see your history here!
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="overflow-auto h-58">
                  <table className="min-w-full bg-grey-lining border">
                    <thead className="bg-indigo-500 border border-gray-200 sticky top-0">
                      <tr className="table-row">
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase sticky-col">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            Index
                          </div>
                        </th>
                        <th
                          onClick={() => handleSortByField("title")}
                          className="table-header-cell"
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            className="relative"
                          >
                            Job Title{" "}
                            {sorted["title"] === 0 ? (
                              <ArrowDownNarrowWide className="min-w-5 absolute right" />
                            ) : sorted["title"] === 1 ? (
                              <ArrowDownWideNarrow className="min-w-5 absolute right" />
                            ) : (
                              <ListFilter className="min-w-5 absolute right" />
                            )}
                          </div>
                        </th>
                        <th
                          onClick={() => handleSortByField("department")}
                          className="table-header-cell"
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            className="relative"
                          >
                            Department{" "}
                            {sorted["department"] === 0 ? (
                              <ArrowDownNarrowWide className="min-w-5 absolute right" />
                            ) : sorted["department"] === 1 ? (
                              <ArrowDownWideNarrow className="min-w-5 absolute right" />
                            ) : (
                              <ListFilter className="min-w-5 absolute right" />
                            )}
                          </div>
                        </th>
                        <th
                          onClick={() => handleSortByField("company")}
                          className="table-header-cell"
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            className="relative"
                          >
                            Company{" "}
                            {sorted["company"] === 0 ? (
                              <ArrowDownNarrowWide className="min-w-5 absolute right" />
                            ) : sorted["company"] === 1 ? (
                              <ArrowDownWideNarrow className="min-w-5 absolute right" />
                            ) : (
                              <ListFilter className="min-w-5 absolute right" />
                            )}
                          </div>
                        </th>
                        <th
                          onClick={() => handleSortByField("location")}
                          className="table-header-cell"
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            className="relative"
                          >
                            Location{" "}
                            {sorted["location"] === 0 ? (
                              <ArrowDownNarrowWide className="min-w-5 absolute right" />
                            ) : sorted["location"] === 1 ? (
                              <ArrowDownWideNarrow className="min-w-5 absolute right" />
                            ) : (
                              <ListFilter className="min-w-5 absolute right" />
                            )}
                          </div>
                        </th>
                        <th
                          onClick={() => handleSortByField("match_score")}
                          className="table-header-cell"
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            className="relative"
                          >
                            Initial ATS Score{" "}
                            {sorted["match_score"] === 0 ? (
                              <ArrowDownNarrowWide className="min-w-5 absolute right" />
                            ) : sorted["match_score"] === 1 ? (
                              <ArrowDownWideNarrow className="min-w-5 absolute right" />
                            ) : (
                              <ListFilter className="min-w-5 absolute right" />
                            )}
                          </div>
                        </th>
                        <th
                          onClick={() => handleSortByField("application_type")}
                          className="table-header-cell"
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            className="relative"
                          >
                            Application Type{" "}
                            {sorted["application_type"] === 0 ? (
                              <ArrowDownNarrowWide className="min-w-5 absolute right" />
                            ) : sorted["application_type"] === 1 ? (
                              <ArrowDownWideNarrow className="min-w-5 absolute right" />
                            ) : (
                              <ListFilter className="min-w-5 absolute right" />
                            )}
                          </div>
                        </th>
                        <th
                          onClick={() => handleSortByField("resume_used")}
                          className="table-header-cell"
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            className="relative"
                          >
                            Resume{" "}
                            {sorted["resume_used"] === 0 ? (
                              <ArrowDownNarrowWide className="min-w-5 absolute right" />
                            ) : sorted["resume_used"] === 1 ? (
                              <ArrowDownWideNarrow className="min-w-5 absolute right" />
                            ) : (
                              <ListFilter className="min-w-5 absolute right" />
                            )}
                          </div>
                        </th>
                        <th
                          onClick={() => handleSortByField("cover_letter_used")}
                          className="table-header-cell"
                          disabled={
                            subscriptionCounts.cover_letters_limit -
                              subscriptionCounts.cover_letters_generated <=
                            0
                          }
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            className="relative"
                          >
                            Cover Letter{" "}
                            {sorted["cover_letter_used"] === 0 ? (
                              <ArrowDownNarrowWide className="min-w-5 absolute right" />
                            ) : sorted["cover_letter_used"] === 1 ? (
                              <ArrowDownWideNarrow className="min-w-5 absolute right" />
                            ) : (
                              <ListFilter className="min-w-5 absolute right" />
                            )}
                          </div>
                        </th>
                        <th
                          onClick={() => handleSortByField("ats_score")}
                          className="table-header-cell"
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            className="relative"
                          >
                            Improved ATS Score{" "}
                            {sorted["ats_score"] === 0 ? (
                              <ArrowDownNarrowWide className="min-w-5 absolute right" />
                            ) : sorted["ats_score"] === 1 ? (
                              <ArrowDownWideNarrow className="min-w-5 absolute right" />
                            ) : (
                              <ListFilter className="min-w-5 absolute right" />
                            )}
                          </div>
                        </th>
                        <th
                          onClick={() => handleSortByField("applied")}
                          className="table-header-cell"
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "4px",
                            }}
                            className="relative"
                          >
                            Applied?{" "}
                            {sorted["applied"] === 0 ? (
                              <ArrowDownNarrowWide className="min-w-5 absolute right" />
                            ) : sorted["applied"] === 1 ? (
                              <ArrowDownWideNarrow className="min-w-5 absolute right" />
                            ) : (
                              <ListFilter className="min-w-5 absolute right" />
                            )}
                          </div>
                        </th>
                        <th
                          onClick={() => handleSortByField("application_date")}
                          className="table-header-cell"
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            className="relative"
                          >
                            Applied at{" "}
                            {sorted["application_date"] === 0 ? (
                              <ArrowDownNarrowWide className="min-w-5 absolute right" />
                            ) : sorted["application_date"] === 1 ? (
                              <ArrowDownWideNarrow className="min-w-5 absolute right" />
                            ) : (
                              <ListFilter className="min-w-5 absolute right" />
                            )}
                          </div>
                        </th>
                        <th
                          onClick={() => handleSortByField("tailored")}
                          className="table-header-cell"
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "4px",
                            }}
                            className="relative"
                          >
                            Tailored?{" "}
                            {sorted["tailored"] === 0 ? (
                              <ArrowDownNarrowWide className="min-w-5 absolute right" />
                            ) : sorted["tailored"] === 1 ? (
                              <ArrowDownWideNarrow className="min-w-5 absolute right" />
                            ) : (
                              <ListFilter className="min-w-5 absolute right" />
                            )}
                          </div>
                        </th>
                        <th
                          onClick={() => handleSortByField("tailored_at")}
                          className="table-header-cell"
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            className="relative"
                          >
                            Tailored at{" "}
                            {sorted["tailored_at"] === 0 ? (
                              <ArrowDownNarrowWide className="min-w-5 absolute right" />
                            ) : sorted["tailored_at"] === 1 ? (
                              <ArrowDownWideNarrow className="min-w-5 absolute right" />
                            ) : (
                              <ListFilter className="min-w-5 absolute right" />
                            )}
                          </div>
                        </th>
                        <th
                          onClick={() => handleSortByField("referred")}
                          className="table-header-cell"
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "4px",
                            }}
                            className="relative"
                          >
                            Got Referral?{" "}
                            {sorted["referred"] === 0 ? (
                              <ArrowDownNarrowWide className="min-w-5 absolute right" />
                            ) : sorted["referred"] === 1 ? (
                              <ArrowDownWideNarrow className="min-w-5 absolute right" />
                            ) : (
                              <ListFilter className="min-w-5 absolute right" />
                            )}
                          </div>
                        </th>
                        <th
                          onClick={() => handleSortByField("result")}
                          className="table-header-cell"
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              minWidth: "5rem",
                            }}
                            className="relative"
                          >
                            Application Result{" "}
                            {sorted["result"] === 0 ? (
                              <ArrowDownNarrowWide className="min-w-5 absolute right" />
                            ) : sorted["result"] === 1 ? (
                              <ArrowDownWideNarrow className="min-w-5 absolute right" />
                            ) : (
                              <ListFilter className="min-w-5 absolute right" />
                            )}
                          </div>
                        </th>
                        <th className="px-4 py-3 cursor-pointer"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchedApps.map((app, index) =>
                        savingAppId === app.application_id ? (
                          <tr
                            key={app.application_id || index}
                            className="bg-gray-100 table-row"
                          >
                            <td className="px-4 py-3 text-center text-sm text-gray-600">
                              Saving changes...
                            </td>
                          </tr>
                        ) : (
                          <tr
                            key={app.application_id || index}
                            className="table-row"
                          >
                            <td className="px-4 py-3 bg-indigo-500 text-sm text-white sticky-col">
                              {index + 1}
                            </td>
                            <td className="table-cell">
                              {app.url !== "" ? (
                                <a
                                  href={app.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {app.title.split(",")[0]}
                                </a>
                              ) : (
                                app.title.split(",")[0]
                              )}
                            </td>
                            <td className="table-cell">
                              {app.department || "--"}
                            </td>
                            <td className="table-cell">
                              {app.company || "--"}
                            </td>
                            <td className="table-cell">{app.location}</td>
                            <td className="table-cell" title={app.match_score}>
                              {app.match_score === 0 ? "--" : app.match_score}
                            </td>
                            <td className="table-cell">
                              {app.application_type || "--"}
                            </td>
                            <td className="table-cell">
                              {app.resume_name && app.resume_name}
                              <div className="buttons-container gap-4">
                                <Download
                                  className="text-green-800 w-5 h-5 cursor-pointer"
                                  onClick={() =>
                                    handleDownloadPdf(
                                      false,
                                      app.resume_used,
                                      app,
                                    )
                                  }
                                />
                              </div>
                            </td>
                            <td className="table-cell">
                              {app.cover_letter_name && app.cover_letter_name}
                              <div className="buttons-container gap-2">
                                {subscriptionCounts.cover_letters_limit -
                                  subscriptionCounts.cover_letters_generated >
                                0 ? (
                                  isGeneratingCoverLetter &&
                                  appBeingModified === app.application_id ? (
                                    <p>Generating...</p>
                                  ) : (
                                    <Download
                                      className="text-green-800 w-5 h-5 cursor-pointer"
                                      onClick={async () => {
                                        setAppBeingModified(app.application_id);
                                        const appResult =
                                          await handleDownloadCoverLetter(
                                            app.resume_used,
                                            "",
                                            app,
                                          );
                                        const updater = (prev) =>
                                          prev.map((a) =>
                                            a.application_id ===
                                            app.application_id
                                              ? {
                                                  ...a,
                                                  cover_letter_name:
                                                    appResult?.cover_letter_name ??
                                                    a.cover_letter_name,
                                                  cover_letter_used:
                                                    appResult?.cover_letter_used ??
                                                    a.cover_letter_used,
                                                }
                                              : a,
                                          );
                                        setSearchedApps(updater);
                                        setApplicationHistory(updater);
                                      }}
                                    />
                                  )
                                ) : (
                                  "--"
                                )}
                              </div>
                            </td>
                            <td
                              className="table-cell truncate max-w-xs"
                              title={app.ats_score}
                            >
                              {app.ats_score || "--"}
                            </td>
                            <td className="table-cell text-center">
                              <input
                                type="checkbox"
                                checked={app.applied}
                                onChange={(e) => {
                                  setUserStats((prev) => ({
                                    ...prev,
                                    total_applications:
                                      prev.total_applications +
                                      (e.target.checked ? 1 : -1),
                                  }));
                                  handleApplicationUpdate(app.application_id, {
                                    applied: e.target.checked,
                                  });
                                }}
                                className="h-4 w-4 text-indigo-600 rounded"
                              />
                            </td>
                            <td className="table-cell">
                              {app.application_date
                                ? new Date(
                                    app.application_date?.$date ||
                                      app.application_date,
                                  ).toLocaleDateString()
                                : "--/--/----"}
                            </td>
                            <td className="table-cell truncate max-w-xs">
                              {app.tailored ? "True" : "False"}
                            </td>
                            <td className="table-cell">
                              {app.tailored_at
                                ? new Date(
                                    app.tailored_at?.$date || app.tailored_at,
                                  ).toLocaleDateString()
                                : "--/--/----"}
                            </td>
                            <td className="table-cell text-center">
                              <input
                                type="checkbox"
                                checked={app.referred}
                                onChange={(e) =>
                                  handleApplicationUpdate(app.application_id, {
                                    referred: e.target.checked,
                                  })
                                }
                                className="h-4 w-4 text-indigo-600 rounded"
                              />
                            </td>
                            <td className="table-cell">
                              <select
                                className="text-sm border border-gray-300 rounded px-2 py-1"
                                defaultValue={app.result || "pending"}
                                onChange={(e) =>
                                  handleApplicationUpdate(app.application_id, {
                                    result: e.target.value,
                                  })
                                }
                              >
                                <option value="pending">Pending</option>
                                <option value="rejected">
                                  Rejected (Initial)
                                </option>
                                <option value="phoneScreen">
                                  Phone Screen
                                </option>
                                <option value="phoneRejected">
                                  Rejected (Phone)
                                </option>
                                <option value="technical">
                                  Technical Interview
                                </option>
                                <option value="techRejected">
                                  Rejected (Tech)
                                </option>
                                <option value="onsite">Onsite Interview</option>
                                <option value="onsiteRejected">
                                  Rejected (Onsite)
                                </option>
                                <option value="offers">Got an offer</option>
                                <option value="accepted">Accepted Offer</option>
                              </select>
                            </td>
                            <td className="table-cell">
                              <button
                                className="buttons-red-mini"
                                onClick={() => deleteApplication(app)}
                              >
                                <SquareX className="w-5 h-5" />
                              </button>
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationHistoryScreen;
